<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

export default {
  name: 'PlanSelectionModal',
  data() {
    return {
      isUpgrading: false,
      selectedPlanId: null,
      showUpgradeErrorModal: false,
      upgradeErrorMessage: '',
    };
  },
  computed: {
    ...mapGetters('license', [
      'availablePlans',
      'isLoadingPlans',
      'licenseStatus',
      'currentPlanName',
      'installationIdentifier',
    ]),
    ...mapGetters({
      globalConfig: 'globalConfig/get',
    }),
    plans() {
      return this.availablePlans || [];
    },
    hasPlans() {
      return this.plans.length > 0;
    },
    topRowPlans() {
      return this.plans.slice(0, 2);
    },
    bottomRowPlans() {
      return this.plans.slice(2, 5);
    },
    isTrial() {
      return ['trial', 'trialing'].includes(this.licenseStatus);
    },
    currentPlanDisplayName() {
      return this.currentPlanName?.toString().toUpperCase();
    },
    blockReason() {
      return this.$store.state.license.reason || 'LICENSE_BLOCKED';
    },
    normalizedBlockState() {
      const reason = this.blockReason.toString();
      if (this.isTrial) return 'TRIAL';
      if (['LICENSE_EXPIRED', 'TRIAL_EXPIRED'].includes(reason)) {
        return 'EXPIRED';
      }
      if (
        ['DOMAIN_MISMATCH', 'INSTALLATION_MISMATCH', 'MACHINE_MISMATCH'].some(
          code => reason.includes(code)
        )
      ) {
        return 'INSTALLATION_MISMATCH';
      }
      if (reason.includes('LICENSE_NOT_FOUND')) return 'LICENSE_NOT_FOUND';
      if (reason.includes('LICENSE_SUSPENDED')) return 'LICENSE_SUSPENDED';
      if (reason.includes('NETWORK')) return 'VALIDATION_UNAVAILABLE';
      return 'GENERIC';
    },
    isInstallationMismatch() {
      return this.normalizedBlockState === 'INSTALLATION_MISMATCH';
    },
    isRecoverableValidationIssue() {
      return ['INSTALLATION_MISMATCH', 'LICENSE_NOT_FOUND', 'GENERIC'].includes(
        this.normalizedBlockState
      );
    },
    canSelectPlan() {
      return !['INSTALLATION_MISMATCH', 'LICENSE_SUSPENDED'].includes(
        this.normalizedBlockState
      );
    },
    blockBadge() {
      return this.licenseStateText('BADGE');
    },
    blockTitle() {
      if (this.isTrial) {
        return this.licenseStateText('TITLE', {
          days: this.timeLeftString,
          time: this.timeLeftString,
        });
      }
      return this.replaceInstallationName(this.licenseStateText('TITLE'));
    },
    blockDescription() {
      return this.replaceInstallationName(this.licenseStateText('DESCRIPTION'));
    },
    blockHint() {
      return this.replaceInstallationName(this.licenseStateText('HINT'));
    },
    brandName() {
      return this.globalConfig?.installationName || 'Chatplan';
    },
    timeLeftString() {
      const expiresAt = this.$store.state.license.expiresAt;
      if (!expiresAt) {
        return this.$t('LICENSE.PAYWALL.DAYS_LEFT', { count: 14 });
      }
      const now = new Date();
      const exp = new Date(expiresAt);
      const diffMs = exp - now;

      if (diffMs <= 0) {
        return this.$t('LICENSE.PAYWALL.MINUTES_LEFT', { count: 0 });
      }

      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays > 1) {
        return this.$t('LICENSE.PAYWALL.DAYS_LEFT', { count: diffDays });
      }
      if (diffHours >= 1) {
        return diffHours === 1
          ? this.$t('LICENSE.PAYWALL.HOUR_LEFT', { count: diffHours })
          : this.$t('LICENSE.PAYWALL.HOURS_LEFT', { count: diffHours });
      }
      return diffMins === 1
        ? this.$t('LICENSE.PAYWALL.MINUTE_LEFT', { count: diffMins })
        : this.$t('LICENSE.PAYWALL.MINUTES_LEFT', { count: diffMins });
    },
    expiresAtFormatted() {
      const expiresAt = this.$store.state.license.expiresAt;
      if (!expiresAt) return '';
      const exp = new Date(expiresAt);
      return exp.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  mounted() {
    this.$store.dispatch('license/fetchPlans');
  },
  methods: {
    async handleUpgrade(planId) {
      if (this.isUpgrading) return;
      this.isUpgrading = true;
      this.selectedPlanId = planId;
      const result = await this.$store.dispatch('license/upgradePlan', planId);
      this.isUpgrading = false;
      this.selectedPlanId = null;
      if (result.success) {
        this.showUpgradeSuccess(planId);
      } else {
        this.showUpgradeError(result);
      }
    },
    showUpgradeSuccess(planId) {
      const selectedPlan = this.plans.find(plan => plan.id === planId);
      useAlert(
        this.$t('LICENSE.PAYWALL.UPGRADE_SUCCESS', {
          plan: selectedPlan?.name || this.$t('LICENSE.PAYWALL.SELECTED_PLAN'),
        })
      );
    },
    showUpgradeError(result = {}) {
      this.upgradeErrorMessage =
        result.status === 401
          ? this.$t('LICENSE.PAYWALL.UPGRADE_UNAUTHORIZED')
          : this.$t('LICENSE.PAYWALL.UPGRADE_ERROR');
      this.showUpgradeErrorModal = true;
    },
    closeUpgradeErrorModal() {
      this.showUpgradeErrorModal = false;
      this.upgradeErrorMessage = '';
    },
    async retryValidation() {
      await this.$store.dispatch('license/validateLicense');
      if (!this.$store.state.license.blocked) {
        window.location.reload();
      }
    },
    async copyInstallationIdentifier() {
      if (!this.installationIdentifier) return;
      await copyTextToClipboard(this.installationIdentifier);
      useAlert(this.$t('LICENSE.PAYWALL.COPY_IDENTIFIER_SUCCESS'));
    },
    licenseStateText(field, values = {}) {
      const states = {
        TRIAL: {
          BADGE: this.$t('LICENSE.PAYWALL.STATES.TRIAL.BADGE', values),
          TITLE: this.$t('LICENSE.PAYWALL.STATES.TRIAL.TITLE', values),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.TRIAL.DESCRIPTION',
            values
          ),
          HINT: this.$t('LICENSE.PAYWALL.STATES.TRIAL.HINT', values),
        },
        EXPIRED: {
          BADGE: this.$t('LICENSE.PAYWALL.STATES.EXPIRED.BADGE', values),
          TITLE: this.$t('LICENSE.PAYWALL.STATES.EXPIRED.TITLE', values),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.EXPIRED.DESCRIPTION',
            values
          ),
          HINT: this.$t('LICENSE.PAYWALL.STATES.EXPIRED.HINT', values),
        },
        INSTALLATION_MISMATCH: {
          BADGE: this.$t(
            'LICENSE.PAYWALL.STATES.INSTALLATION_MISMATCH.BADGE',
            values
          ),
          TITLE: this.$t(
            'LICENSE.PAYWALL.STATES.INSTALLATION_MISMATCH.TITLE',
            values
          ),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.INSTALLATION_MISMATCH.DESCRIPTION',
            values
          ),
          HINT: this.$t(
            'LICENSE.PAYWALL.STATES.INSTALLATION_MISMATCH.HINT',
            values
          ),
        },
        LICENSE_NOT_FOUND: {
          BADGE: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_NOT_FOUND.BADGE',
            values
          ),
          TITLE: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_NOT_FOUND.TITLE',
            values
          ),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_NOT_FOUND.DESCRIPTION',
            values
          ),
          HINT: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_NOT_FOUND.HINT',
            values
          ),
        },
        LICENSE_SUSPENDED: {
          BADGE: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_SUSPENDED.BADGE',
            values
          ),
          TITLE: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_SUSPENDED.TITLE',
            values
          ),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_SUSPENDED.DESCRIPTION',
            values
          ),
          HINT: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_SUSPENDED.HINT',
            values
          ),
        },
        VALIDATION_UNAVAILABLE: {
          BADGE: this.$t(
            'LICENSE.PAYWALL.STATES.VALIDATION_UNAVAILABLE.BADGE',
            values
          ),
          TITLE: this.$t(
            'LICENSE.PAYWALL.STATES.VALIDATION_UNAVAILABLE.TITLE',
            values
          ),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.VALIDATION_UNAVAILABLE.DESCRIPTION',
            values
          ),
          HINT: this.$t(
            'LICENSE.PAYWALL.STATES.VALIDATION_UNAVAILABLE.HINT',
            values
          ),
        },
        GENERIC: {
          BADGE: this.$t('LICENSE.PAYWALL.STATES.GENERIC.BADGE', values),
          TITLE: this.$t('LICENSE.PAYWALL.STATES.GENERIC.TITLE', values),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.GENERIC.DESCRIPTION',
            values
          ),
          HINT: this.$t('LICENSE.PAYWALL.STATES.GENERIC.HINT', values),
        },
      };
      return (
        states[this.normalizedBlockState]?.[field] || states.GENERIC[field]
      );
    },
    dismissTrial() {
      this.$store.dispatch('license/dismissPaywall');
    },
    replaceInstallationName(text) {
      if (!text) return text;
      return text
        .replace(/Chatwoot/g, this.brandName)
        .replace(/ChatPlan/g, this.brandName)
        .replace(/Chatplan/g, this.brandName);
    },
    formatPrice(cents) {
      return (cents / 100).toFixed(2).replace('.', ',');
    },
    priceLabel(plan) {
      return this.$t('LICENSE.PAYWALL.PRICE_PER_MONTH', {
        price: this.formatPrice(plan.price_in_cents),
      });
    },
    formatSLA(responseHours, resolutionHours) {
      if (!responseHours || !resolutionHours) return '';
      const formatHour = h =>
        h >= 120 && h % 24 === 0 ? `${h / 24}d` : `${h}h`;
      return `${formatHour(responseHours)}/${formatHour(resolutionHours)}`;
    },
    formatAccounts(maxAccounts) {
      if (!maxAccounts || maxAccounts >= 999999) {
        return this.$t('LICENSE.PAYWALL.UNLIMITED_ACCOUNTS');
      }
      if (maxAccounts === 1) {
        return this.$t('LICENSE.PAYWALL.SINGLE_ACCOUNT');
      }
      return this.$t('LICENSE.PAYWALL.ACCOUNTS', { count: maxAccounts });
    },
    featureLabel(key) {
      if (key === 'white_label') {
        return this.$t('LICENSE.PAYWALL.FEATURES.WHITE_LABEL');
      }
      if (key === 'cortex_integration') {
        return this.$t('LICENSE.PAYWALL.FEATURES.CORTEX_INTEGRATION');
      }
      if (key === 'kanban') {
        return this.$t('LICENSE.PAYWALL.FEATURES.KANBAN');
      }
      if (key === 'flowbuilder') {
        return this.$t('LICENSE.PAYWALL.FEATURES.FLOWBUILDER');
      }
      return key;
    },
    normalizedFeatureKey(key) {
      if (key === 'white_label') return key;
      if (key === 'kanban') return key;
      if (key === 'flowbuilder') return key;
      if (key === 'cortex_integration' || key?.startsWith('cortex_')) {
        return 'cortex_integration';
      }
      return key;
    },
    displayFeatures(features = []) {
      return [...new Set(features.map(this.normalizedFeatureKey))];
    },
    planDescription(plan) {
      if (plan.name === 'BASIC') {
        return this.$t('LICENSE.PAYWALL.PLANS_DESCRIPTIONS.BASIC');
      }
      if (plan.name === 'PREMIUM') {
        return this.$t('LICENSE.PAYWALL.PLANS_DESCRIPTIONS.PREMIUM');
      }
      if (plan.name === 'ENTERPRISE') {
        return this.$t('LICENSE.PAYWALL.PLANS_DESCRIPTIONS.ENTERPRISE');
      }
      if (plan.name === 'AGENCY') {
        return this.$t('LICENSE.PAYWALL.PLANS_DESCRIPTIONS.AGENCY');
      }
      if (plan.name === 'UNLIMITED') {
        return this.$t('LICENSE.PAYWALL.PLANS_DESCRIPTIONS.UNLIMITED');
      }
      return plan.description;
    },
    planNameKey(plan) {
      return plan?.name?.toString().toUpperCase();
    },
    isCurrentPlan(plan) {
      return this.currentPlanDisplayName === this.planNameKey(plan);
    },
  },
};
</script>

<template>
  <div
    id="chatplan-license-paywall"
    class="fixed inset-0 z-[99999] flex items-center justify-center bg-n-background/95 backdrop-blur-sm overflow-y-auto"
  >
    <div
      class="w-full mx-auto px-6 py-8"
      :class="hasPlans ? 'max-w-6xl' : 'max-w-md'"
    >
      <div
        class="grid gap-3 auto-rows-min"
        :class="hasPlans ? 'grid-cols-6' : 'grid-cols-1'"
      >
        <!-- Card Informativo -->
        <!-- Card Informativo -->
        <div
          class="rounded-xl border p-6 flex flex-col justify-center items-start shadow-sm bg-n-brand/5 border-n-brand/20 dark:bg-n-brand/10 dark:border-n-brand/30"
          :class="hasPlans ? 'col-span-2' : 'col-span-1'"
        >
          <div class="w-full flex flex-col gap-4">
            <div>
              <div class="flex items-center gap-2 mb-4">
                <span
                  v-if="currentPlanDisplayName"
                  class="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider bg-n-brand/10 text-n-brand ring-1 ring-n-brand/30"
                >
                  {{ currentPlanDisplayName }}
                </span>

                <span
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider"
                  :class="
                    isTrial
                      ? 'bg-n-amber-9/20 text-n-amber-9 ring-1 ring-n-amber-9/30'
                      : 'bg-n-ruby-9/20 text-n-ruby-9 ring-1 ring-n-ruby-9/30'
                  "
                >
                  <span
                    class="size-2 rounded-full"
                    :class="
                      isTrial ? 'bg-n-amber-9 animate-pulse' : 'bg-n-ruby-9'
                    "
                  />
                  {{ blockBadge }}
                </span>
              </div>

              <h2
                class="text-2xl font-bold text-n-slate-12 mb-2 tracking-tight"
              >
                {{ blockTitle }}
              </h2>

              <p
                v-if="isTrial && expiresAtFormatted"
                class="text-[13px] text-n-slate-12 font-semibold mb-2"
              >
                {{
                  $t('LICENSE.PAYWALL.TRIAL_ENDS_AT', {
                    date: expiresAtFormatted,
                  })
                }}
              </p>

              <p class="text-[13px] text-n-slate-11 leading-relaxed">
                {{ blockDescription }}
              </p>

              <p
                v-if="!isTrial"
                class="mt-3 rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-[12px] text-n-slate-11"
              >
                {{ blockHint }}
              </p>

              <div
                v-if="installationIdentifier && isInstallationMismatch"
                class="mt-3 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
              >
                <p class="text-[10px] font-semibold uppercase text-n-slate-10">
                  {{ $t('LICENSE.PAYWALL.INSTALLATION_IDENTIFIER') }}
                </p>
                <p class="mt-1 break-all font-mono text-[11px] text-n-slate-12">
                  {{ installationIdentifier }}
                </p>
              </div>
            </div>

            <button
              v-if="isTrial"
              class="w-full mt-4 flex justify-center items-center gap-2 text-[13px] text-n-brand hover:text-white transition-all bg-n-brand/10 hover:bg-n-brand border border-n-brand/30 hover:border-n-brand hover:-translate-y-0.5 rounded-xl px-4 py-3 font-semibold shadow-sm hover:shadow-n-brand/20"
              @click="dismissTrial"
            >
              {{ $t('LICENSE.PAYWALL.CONTINUE_TRIAL') }}
            </button>

            <div v-else class="mt-4 flex w-full flex-col gap-2">
              <button
                v-if="installationIdentifier && isInstallationMismatch"
                class="flex w-full items-center justify-center gap-2 rounded-xl border border-n-brand/30 bg-n-brand/10 px-4 py-3 text-[13px] font-semibold text-n-brand transition-all hover:bg-n-brand hover:text-white"
                @click="copyInstallationIdentifier"
              >
                <span class="i-lucide-copy size-4" />
                {{ $t('LICENSE.PAYWALL.COPY_IDENTIFIER') }}
              </button>
              <button
                v-if="isRecoverableValidationIssue"
                class="flex w-full items-center justify-center gap-2 rounded-xl border border-n-weak bg-n-alpha-2 px-4 py-3 text-[13px] font-semibold text-n-slate-12 transition-all hover:border-n-brand hover:text-n-brand"
                @click="retryValidation"
              >
                <span class="i-lucide-refresh-cw size-4" />
                {{ $t('LICENSE.PAYWALL.RETRY_VALIDATION') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div
          v-if="isLoadingPlans"
          class="flex items-center justify-center"
          :class="hasPlans ? 'col-span-4' : 'col-span-1'"
        >
          <div
            class="animate-spin rounded-full h-7 w-7 border-2 border-n-brand border-t-transparent"
          />
        </div>

        <!-- Sem planos -->
        <div
          v-else-if="!hasPlans"
          class="col-span-1 rounded-lg border border-n-weak bg-n-surface-2 px-4 py-3 text-center"
        >
          <p class="text-sm text-n-slate-11">
            {{ $t('LICENSE.PAYWALL.PLANS_UNAVAILABLE') }}
          </p>
        </div>

        <template v-else>
          <!-- Row 1: 2 planos -->
          <div
            v-for="plan in topRowPlans"
            :key="plan.id"
            class="col-span-2 relative flex flex-col rounded-xl border transition-all duration-200 p-4"
            :class="[
              plan.name === 'PREMIUM'
                ? 'border-n-brand bg-n-surface-2 shadow-sm shadow-n-brand/10'
                : 'border-n-weak bg-n-surface-2 hover:border-n-strong',
            ]"
          >
            <div v-if="plan.name === 'PREMIUM'" class="absolute -top-2 left-3">
              <span
                class="bg-n-brand text-white text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-md shadow-n-brand/20"
              >
                {{ $t('LICENSE.PAYWALL.RECOMMENDED') }}
              </span>
            </div>
            <h3
              class="text-xs font-semibold text-n-slate-12 uppercase tracking-wider mb-0.5"
            >
              {{ plan.name }}
            </h3>
            <p
              class="text-[11px] text-n-slate-10 mb-3 min-h-[2rem] line-clamp-2"
            >
              {{ planDescription(plan) }}
            </p>
            <div
              class="flex items-baseline gap-1 mb-3 pb-3 border-b border-n-weak/50"
            >
              <span class="text-xl font-bold text-n-slate-12">
                {{ priceLabel(plan) }}
              </span>
            </div>

            <div class="mb-3 space-y-1">
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-building-2 size-3 text-n-slate-9" />
                <span class="text-[11px] font-medium text-n-slate-11">
                  {{ formatAccounts(plan.max_accounts) }}
                </span>
              </div>
              <div
                v-if="plan.sla_response_hours"
                class="flex items-center gap-1.5"
              >
                <span class="i-lucide-clock size-3 text-n-slate-9" />
                <span class="text-[11px] font-medium text-n-slate-11">
                  {{
                    $t('LICENSE.PAYWALL.SLA_LABEL', {
                      format: formatSLA(
                        plan.sla_response_hours,
                        plan.sla_resolution_hours
                      ),
                    })
                  }}
                </span>
              </div>
            </div>

            <div class="flex-1 flex flex-col mb-4">
              <ul
                v-if="plan.features && plan.features.length"
                class="space-y-1.5 mb-3"
              >
                <li
                  v-for="(feat, idx) in displayFeatures(plan.features)"
                  :key="idx"
                  class="flex items-center gap-1.5 text-xs text-n-slate-11"
                >
                  <span
                    class="i-lucide-check size-3 text-n-brand flex-shrink-0"
                  />
                  {{ featureLabel(feat) }}
                </li>
              </ul>
              <div class="mt-1">
                <p class="text-[11px] text-n-slate-10 italic">
                  {{
                    $t('LICENSE.PAYWALL.TRIAL_INCLUDED', {
                      days: plan.trial_days || 14,
                    })
                  }}
                </p>
              </div>
            </div>
            <div class="mt-auto pt-4 flex gap-2 w-full">
              <button
                :disabled="
                  !isTrial ||
                  isUpgrading ||
                  isCurrentPlan(plan) ||
                  !canSelectPlan
                "
                class="flex-1 py-2 px-2 rounded-lg text-[11px] font-medium transition-all duration-150 bg-n-brand/10 border border-n-brand/30 text-n-brand hover:bg-n-brand/20 hover:border-n-brand/50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                @click="handleUpgrade(plan.id)"
              >
                <span v-if="isCurrentPlan(plan)">
                  {{ $t('LICENSE.PAYWALL.CURRENT_PLAN') }}
                </span>
                <span v-else>{{ $t('LICENSE.PAYWALL.TEST_NOW') }}</span>
              </button>
              <button
                :disabled="isUpgrading || !canSelectPlan"
                class="flex-1 py-1.5 px-2 rounded-lg text-[11px] font-semibold transition-all duration-200 whitespace-nowrap"
                :class="[
                  plan.name === 'PREMIUM'
                    ? 'bg-n-brand text-white hover:brightness-110 border border-transparent shadow-sm hover:shadow-lg hover:shadow-n-brand/30 hover:-translate-y-0.5'
                    : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-brand hover:text-white border border-n-weak hover:border-n-brand shadow-sm hover:shadow-lg hover:shadow-n-brand/20 hover:-translate-y-0.5',
                  isUpgrading ? 'opacity-50 cursor-not-allowed' : '',
                ]"
                @click="handleUpgrade(plan.id)"
              >
                <span v-if="isUpgrading && selectedPlanId === plan.id">
                  {{ $t('LICENSE.PAYWALL.PROCESSING') }}
                </span>
                <span v-else>{{ $t('LICENSE.PAYWALL.SUBSCRIBE') }}</span>
              </button>
            </div>
          </div>

          <!-- Row 2: 3 planos -->
          <div
            v-for="plan in bottomRowPlans"
            :key="plan.id"
            class="col-span-2 relative flex flex-col rounded-xl border transition-all duration-200 p-4"
            :class="[
              plan.name === 'PREMIUM'
                ? 'border-n-brand bg-n-surface-2 shadow-sm shadow-n-brand/10'
                : 'border-n-weak bg-n-surface-2 hover:border-n-strong',
            ]"
          >
            <div v-if="plan.name === 'PREMIUM'" class="absolute -top-2 left-3">
              <span
                class="bg-n-brand text-white text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-md shadow-n-brand/20"
              >
                {{ $t('LICENSE.PAYWALL.RECOMMENDED') }}
              </span>
            </div>
            <h3
              class="text-xs font-semibold text-n-slate-12 uppercase tracking-wider mb-0.5"
            >
              {{ plan.name }}
            </h3>
            <p
              class="text-[11px] text-n-slate-10 mb-3 min-h-[2rem] line-clamp-2"
            >
              {{ planDescription(plan) }}
            </p>
            <div
              class="flex items-baseline gap-1 mb-3 pb-3 border-b border-n-weak/50"
            >
              <span class="text-xl font-bold text-n-slate-12">
                {{ priceLabel(plan) }}
              </span>
            </div>

            <div class="mb-3 space-y-1">
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-building-2 size-3 text-n-slate-9" />
                <span class="text-[11px] font-medium text-n-slate-11">
                  {{ formatAccounts(plan.max_accounts) }}
                </span>
              </div>
              <div
                v-if="plan.sla_response_hours"
                class="flex items-center gap-1.5"
              >
                <span class="i-lucide-clock size-3 text-n-slate-9" />
                <span class="text-[11px] font-medium text-n-slate-11">
                  {{
                    $t('LICENSE.PAYWALL.SLA_LABEL', {
                      format: formatSLA(
                        plan.sla_response_hours,
                        plan.sla_resolution_hours
                      ),
                    })
                  }}
                </span>
              </div>
            </div>

            <div class="flex-1 flex flex-col mb-4">
              <ul
                v-if="plan.features && plan.features.length"
                class="space-y-1.5 mb-3"
              >
                <li
                  v-for="(feat, idx) in displayFeatures(plan.features)"
                  :key="idx"
                  class="flex items-center gap-1.5 text-xs text-n-slate-11"
                >
                  <span
                    class="i-lucide-check size-3 text-n-brand flex-shrink-0"
                  />
                  {{ featureLabel(feat) }}
                </li>
              </ul>
              <div class="mt-1">
                <p class="text-[11px] text-n-slate-10 italic">
                  {{
                    $t('LICENSE.PAYWALL.TRIAL_INCLUDED', {
                      days: plan.trial_days || 14,
                    })
                  }}
                </p>
              </div>
            </div>
            <div class="mt-auto pt-4 flex gap-2 w-full">
              <button
                :disabled="
                  !isTrial ||
                  isUpgrading ||
                  isCurrentPlan(plan) ||
                  !canSelectPlan
                "
                class="flex-1 py-2 px-2 rounded-lg text-[11px] font-medium transition-all duration-150 bg-n-brand/10 border border-n-brand/30 text-n-brand hover:bg-n-brand/20 hover:border-n-brand/50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                @click="handleUpgrade(plan.id)"
              >
                <span v-if="isCurrentPlan(plan)">
                  {{ $t('LICENSE.PAYWALL.CURRENT_PLAN') }}
                </span>
                <span v-else>{{ $t('LICENSE.PAYWALL.TEST_NOW') }}</span>
              </button>
              <button
                :disabled="isUpgrading || !canSelectPlan"
                class="flex-1 py-1.5 px-2 rounded-lg text-[11px] font-semibold transition-all duration-200 whitespace-nowrap"
                :class="[
                  plan.name === 'PREMIUM'
                    ? 'bg-n-brand text-white hover:brightness-110 border border-transparent shadow-sm hover:shadow-lg hover:shadow-n-brand/30 hover:-translate-y-0.5'
                    : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-brand hover:text-white border border-n-weak hover:border-n-brand shadow-sm hover:shadow-lg hover:shadow-n-brand/20 hover:-translate-y-0.5',
                  isUpgrading ? 'opacity-50 cursor-not-allowed' : '',
                ]"
                @click="handleUpgrade(plan.id)"
              >
                <span v-if="isUpgrading && selectedPlanId === plan.id">
                  {{ $t('LICENSE.PAYWALL.PROCESSING') }}
                </span>
                <span v-else>{{ $t('LICENSE.PAYWALL.SUBSCRIBE') }}</span>
              </button>
            </div>
          </div>
        </template>
      </div>

      <!-- Footer -->
      <div class="mt-3 text-center">
        <p class="text-[11px] text-n-slate-10">
          {{ $t('LICENSE.PAYWALL.CUSTOM_PLAN') }}
          <a href="#" class="text-n-brand hover:underline">
            {{ $t('LICENSE.PAYWALL.CONTACT_SALES') }}
          </a>
        </p>
      </div>
    </div>

    <woot-modal
      v-model:show="showUpgradeErrorModal"
      :on-close="closeUpgradeErrorModal"
    >
      <woot-modal-header
        :header-title="$t('LICENSE.PAYWALL.UPGRADE_ERROR_TITLE')"
        :header-content="upgradeErrorMessage"
      />
      <div class="flex justify-end gap-2 px-6 pb-6">
        <button
          type="button"
          class="rounded-lg bg-n-brand px-4 py-2 text-sm font-semibold text-white transition hover:brightness-110"
          @click="closeUpgradeErrorModal"
        >
          {{ $t('LICENSE.PAYWALL.UPGRADE_ERROR_CLOSE') }}
        </button>
      </div>
    </woot-modal>
  </div>
</template>
